import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";

export function useChallengePreferences() {
  const [loading, setLoading] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const markInterested = async (challengeId: string) => {
    setLoading(challengeId);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Upsert preference
      const { error: prefError } = await supabase
        .from("user_challenge_preferences")
        .upsert({
          user_id: user.id,
          challenge_id: challengeId,
          status: "interested"
        }, { 
          onConflict: "user_id,challenge_id"
        });

      if (prefError) throw prefError;

      // Remove from recommendations
      await supabase
        .from("challenge_recommendations")
        .delete()
        .eq("user_id", user.id)
        .eq("challenge_id", challengeId);

      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: ["challenge-recommendations"] });
      queryClient.invalidateQueries({ queryKey: ["interested-challenges"] });

      toast.success("Saved to Interested");
    } catch (error: any) {
      console.error("Error marking interested:", error);
      toast.error("Failed to save preference");
    } finally {
      setLoading(null);
    }
  };

  const markIgnored = async (challengeId: string) => {
    setLoading(challengeId);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Upsert preference
      const { error: prefError } = await supabase
        .from("user_challenge_preferences")
        .upsert({
          user_id: user.id,
          challenge_id: challengeId,
          status: "ignored"
        }, { 
          onConflict: "user_id,challenge_id"
        });

      if (prefError) throw prefError;

      // Remove from recommendations
      await supabase
        .from("challenge_recommendations")
        .delete()
        .eq("user_id", user.id)
        .eq("challenge_id", challengeId);

      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: ["challenge-recommendations"] });

      toast.success("Challenge hidden from recommendations");
    } catch (error: any) {
      console.error("Error marking ignored:", error);
      toast.error("Failed to save preference");
    } finally {
      setLoading(null);
    }
  };

  const removeInterested = async (challengeId: string) => {
    setLoading(challengeId);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { error } = await supabase
        .from("user_challenge_preferences")
        .delete()
        .eq("user_id", user.id)
        .eq("challenge_id", challengeId);

      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: ["interested-challenges"] });

      toast.success("Removed from Interested");
    } catch (error: any) {
      console.error("Error removing interested:", error);
      toast.error("Failed to remove");
    } finally {
      setLoading(null);
    }
  };

  return {
    markInterested,
    markIgnored,
    removeInterested,
    loading
  };
}
